import type CreativeSDK from '@cesdk/cesdk-js';
import { GQLProductFragment, GQLProductSideFragment } from '@theprintbar/graphql';
import { BrandColours, getPreferredContrastColor, hexToPixel, invariant } from '@theprintbar/shared';
import { getPageId } from './block';

export function getPageSize(side: GQLProductSideFragment) {
	//The Page can be an abritrary size, but we'll use the real world size in case it comes in handy
	let width = side.printWidth;
	let height = side.printHeight;

	invariant(height && width, 'Page height and width must be defined');
	const scaleFactor = Math.max(1000 / width, 1000 / height);
	width = Math.round(width * scaleFactor);
	height = Math.round(height * scaleFactor);

	return { width, height, scaleFactor };
}

export function createPageForSide(
	editor: CreativeSDK,
	scene: number,
	product: GQLProductFragment,
	side: GQLProductSideFragment,
	colourCode?: string,
) {
	const { block } = editor.engine;
	const colour = (product.colours?.find(({ colour }) => colour.code === colourCode) || product.colours?.[0])?.colour;
	if (!colour) throw 'Error finding colour';

	const page = editor.engine.block.create('page');

	const { width, height, scaleFactor } = getPageSize(side);

	//make the page not clickable
	block.setWidth(page, width);
	block.setHeight(page, height);
	block.setMetadata(page, 'scaleFactor', scaleFactor.toString());
	block.setMetadata(page, 'referenceText', side.printReferenceText);
	block.setMetadata(page, 'referenceOffset', side.printReferenceOffset?.toString() || '0');
	block.setMetadata(page, 'referencePosition', side.printReferencePosition || 'Top');
	block.setFillEnabled(page, false);
	block.setStrokeEnabled(page, true);
	block.setStrokePosition(page, 'Inner');
	const strokeColour = getPreferredContrastColor(colour.hex, BrandColours.Blue, BrandColours.Orange);
	const [r, g, b] = hexToPixel(strokeColour);
	block.setStrokeColor(page, { r: r / 255, g: g / 255, b: b / 255, a: 1 });
	block.setStrokeWidth(page, 4);
	block.insertChild(scene, page, 1);
	block.setScopeEnabled(page, 'editor/select', false);

	block.setName(page, getPageId(side.side));

	side.horizontalSnapLines?.structure?.forEach(snapLine => {
		if (!snapLine) return;
		//Insert a random block 50mm down from the top of the page to act as a margin
		const marginBlock = block.create('graphic');
		block.setWidth(marginBlock, width);
		block.setHeight(marginBlock, snapLine * scaleFactor);
		block.setVisible(marginBlock, false);
		block.setScopeEnabled(marginBlock, 'editor/select', false);
		block.appendChild(page, marginBlock);
	});

	side.verticalSnapLines?.structure?.forEach(snapLine => {
		if (!snapLine) return;
		const marginBlock = block.create('graphic');
		block.setWidth(marginBlock, snapLine * scaleFactor);
		block.setHeight(marginBlock, height);
		block.setVisible(marginBlock, false);
		block.setScopeEnabled(marginBlock, 'editor/select', false);
		block.appendChild(page, marginBlock);
	});

	block.setClipped(page, true);
	block.setVisible(page, false);
	return page;
}
